import React, { useState, useLayoutEffect, useEffect, useRef } from 'react';
import {
  BarChart3,
  Settings,
  LogOut,
  Plus,
  ShoppingBasket,
  Star,
  Wifi,
  WifiOff,
  Loader2,
  Gift,
  Mail,
  Users,
} from 'lucide-react';
import { Player as LottiePlayer } from '@lottiefiles/react-lottie-player';
import { cn } from '../poker-ui/utils/cn';
import { LOBBY_HOME_ASSETS } from '../lib/lobbyHomeAssets';

const ONLINE_TILE_BG_SRC = `${process.env.PUBLIC_URL || ''}/background-for-online.png`;
const BOT_TILE_BG_SRC = `${process.env.PUBLIC_URL || ''}/bot-button-background.png`;
const GUEST_AVATAR_SRC = `${process.env.PUBLIC_URL || ''}/guest-avatar.png`;
const FALLBACK_GUEST_AVATAR_SRC = `${process.env.PUBLIC_URL || ''}/avatar-for-bot.png`;
const LOBBY_MODE_SWITCH_PLAY_TILES_ONCE_KEY = 'seka-lobby-mode-switch-play-tiles-once';

export function markLobbyModeSwitchPlayTilesOnce() {
  try {
    sessionStorage.setItem(LOBBY_MODE_SWITCH_PLAY_TILES_ONCE_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

function consumeLobbyModeSwitchPlayTilesOnce() {
  try {
    const value = sessionStorage.getItem(LOBBY_MODE_SWITCH_PLAY_TILES_ONCE_KEY);
    if (!value) return false;
    sessionStorage.removeItem(LOBBY_MODE_SWITCH_PLAY_TILES_ONCE_KEY);
    return true;
  } catch {
    return false;
  }
}

function LobbyModeTileLottie({ src, className, style, playSignal = 0, tileId = 'tile', continuousLoop = false }) {
  const animRef = useRef(null);
  const [reducedMotion, setReducedMotion] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!continuousLoop) return undefined;
    const onVis = () => {
      const inst = animRef.current;
      if (!inst) return;
      if (document.hidden) {
        inst.pause();
      } else if (!reducedMotion) {
        inst.play();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [continuousLoop, reducedMotion]);

  useEffect(() => {
    if (!continuousLoop || !reducedMotion) return undefined;
    const inst = animRef.current;
    if (inst) inst.pause();
    return undefined;
  }, [continuousLoop, reducedMotion]);

  if (continuousLoop) {
    const allowMotion = !reducedMotion;
    return (
      <LottiePlayer
        key={tileId}
        lottieRef={(inst) => {
          animRef.current = inst;
          if (inst && typeof document !== 'undefined' && document.hidden) {
            inst.pause();
          }
        }}
        autoplay={allowMotion}
        loop={allowMotion}
        speed={0.92}
        renderer="svg"
        src={src}
        className={className}
        style={style}
      />
    );
  }

  return (
    <LottiePlayer
      key={`${tileId}-${playSignal}`}
      autoplay={playSignal > 0}
      loop={false}
      src={src}
      className={className}
      style={style}
    />
  );
}

export const MODE_TILES = [
  {
    id: 'local',
    label: 'Играть с ботами',
    sub: '1-3 бота',
    animationSrc: `${process.env.PUBLIC_URL || ''}/robot.json`,
    backgroundSrc: BOT_TILE_BG_SRC,
    /** В тон вкладке «Сека» и кнопке «Создать комнату» (янтарь), не изумруд как в покере */
    accent: 'from-[#d4a84b] via-[#b8892a] to-[#7a5c12] border-amber-500/45',
  },
  {
    id: 'online',
    label: 'Онлайн',
    sub: 'Сеть',
    animationSrc: `${process.env.PUBLIC_URL || ''}/globee.json`,
    backgroundSrc: ONLINE_TILE_BG_SRC,
    accent: 'from-sky-600/90 to-indigo-900/90 border-sky-400/30',
  },
];

/** Покерное лобби: один вход в локальную практику 4-max против ботов (poker-practice). */
export const POKER_LOBBY_PLAY_TILES = [
  {
    id: 'poker-bots',
    label: 'Играть с ботами',
    sub: '4-max · poker-ts',
    animationSrc: `${process.env.PUBLIC_URL || ''}/robot.json`,
    backgroundSrc: BOT_TILE_BG_SRC,
    accent: 'from-emerald-600/90 to-emerald-900/90 border-emerald-400/30',
  },
  {
    id: 'poker-online',
    label: 'Онлайн',
    sub: 'Столы и комнаты',
    animationSrc: `${process.env.PUBLIC_URL || ''}/globee.json`,
    backgroundSrc: ONLINE_TILE_BG_SRC,
    accent: 'from-sky-600/90 to-indigo-900/90 border-sky-400/30',
  },
];

export const BELOT_LOBBY_PLAY_TILES = [
  {
    id: 'belot-bots',
    label: 'Играть с ботами',
    sub: '4 игрока · Белот',
    animationSrc: `${process.env.PUBLIC_URL || ''}/robot.json`,
    backgroundSrc: BOT_TILE_BG_SRC,
    accent: 'from-violet-600/90 to-violet-900/90 border-violet-400/30',
  },
  {
    id: 'belot-online',
    label: 'Онлайн',
    sub: 'Столы и комнаты',
    animationSrc: `${process.env.PUBLIC_URL || ''}/globee.json`,
    backgroundSrc: ONLINE_TILE_BG_SRC,
    accent: 'from-sky-600/90 to-indigo-900/90 border-sky-400/30',
  },
];

const LOBBY_HUB_CREATE_ROOM_LAST_MODE_KEY = 'seka-lobby-hub-create-room-last-mode';

export function normalizeLobbyHubMode(mode) {
  if (mode === 'poker') return 'poker';
  if (mode === 'belot') return 'belot';
  return 'seka';
}

/** Единая шкала лиг игрока для всех звездных бейджей в приложении. */
export function getPlayerLeague(level) {
  const lvl = Number(level) || 1;
  if (lvl >= 65) {
    return {
      key: 'diamond',
      label: 'Алмаз',
      short: 'АЛ',
      starClass: 'fill-cyan-300 stroke-cyan-700',
    };
  }
  if (lvl >= 50) {
    return {
      key: 'ruby',
      label: 'Рубин',
      short: 'РУ',
      starClass: 'fill-rose-400 stroke-rose-800',
    };
  }
  if (lvl >= 35) {
    return {
      key: 'platinum',
      label: 'Платина',
      short: 'ПЛ',
      starClass: 'fill-violet-300 stroke-violet-800',
    };
  }
  if (lvl >= 20) {
    return {
      key: 'gold',
      label: 'Золото',
      short: 'ЗЛ',
      starClass: 'fill-amber-300 stroke-amber-800',
    };
  }
  if (lvl >= 10) {
    return {
      key: 'silver',
      label: 'Серебро',
      short: 'СР',
      starClass: 'fill-slate-300 stroke-slate-700',
    };
  }
  return {
    key: 'bronze',
    label: 'Бронза',
    short: 'БР',
    starClass: 'fill-orange-300 stroke-orange-800',
  };
}

function readLobbyHubLastMode() {
  try {
    const v = sessionStorage.getItem(LOBBY_HUB_CREATE_ROOM_LAST_MODE_KEY);
    if (v === 'seka' || v === 'poker' || v === 'belot') return v;
  } catch {
    /* private mode */
  }
  return null;
}

function writeLobbyHubLastMode(m) {
  try {
    sessionStorage.setItem(LOBBY_HUB_CREATE_ROOM_LAST_MODE_KEY, m);
  } catch {
    /* ignore */
  }
}

/** Только заливка (градиент + внутренняя «стеклянная» кромка) — для слоёв перелива. */
export function lobbyHubCreateRoomButtonFillClass(mode) {
  switch (mode) {
    case 'poker':
      return 'bg-gradient-to-b from-[#34d399] via-[#059669] to-[#064e3b] shadow-[inset_0_1px_0_rgba(255,255,255,0.22)]';
    case 'belot':
      return 'bg-gradient-to-b from-[#a5b4fc] via-[#6366f1] to-[#312e81] shadow-[inset_0_1px_0_rgba(255,255,255,0.22)]';
    default:
      return 'bg-gradient-to-b from-[#d4a84b] via-[#b8892a] to-[#7a5c12] shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]';
  }
}

/** Рамка и внешнее свечение — на самой кнопке, в тон текущему хабу. */
export function lobbyHubCreateRoomButtonFrameClass(mode) {
  switch (mode) {
    case 'poker':
      return 'border-emerald-500/45 shadow-[0_10px_30px_rgba(16,185,129,0.35)]';
    case 'belot':
      return 'border-indigo-400/45 shadow-[0_10px_30px_rgba(99,102,241,0.35)]';
    default:
      return 'border-amber-500/45 shadow-[0_10px_30px_rgba(201,162,39,0.3)]';
  }
}

/**
 * Градиент и тень центральной кнопки «Создать комнату» в нижней панели хаба
 * — в тон активной вкладке `LobbyGameSwitcher` (Сека / Покер / Белот).
 */
export function lobbyHubCreateRoomButtonClass(mode) {
  return cn(
    lobbyHubCreateRoomButtonFrameClass(mode),
    lobbyHubCreateRoomButtonFillClass(mode),
    'hover:brightness-110'
  );
}

/** Общая геометрия premium-нижнего навбара во всех хабах. */
export const LOBBY_BOTTOM_NAV_SHELL_CLASS =
  'fixed bottom-0 left-0 right-0 z-30 border-t border-white/[0.06] bg-[#02050c] px-3 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] pt-2.5 sm:px-4';
export const LOBBY_BOTTOM_NAV_DOCK_CLASS =
  'mx-auto w-full max-w-lg rounded-xl border border-white/[0.07] bg-[#070c14] p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.045),0_0_0_1px_rgba(0,0,0,0.35)] sm:max-w-3xl lg:max-w-5xl';
export const LOBBY_BOTTOM_NAV_GRID_CLASS = 'grid w-full grid-cols-[1fr_auto_1fr] items-center gap-x-1.5 sm:gap-x-2';
export const LOBBY_BOTTOM_NAV_SIDE_BUTTON_BASE_CLASS =
  'lobby-focus relative isolate m-0 flex h-[60px] w-[70px] shrink-0 flex-col items-center justify-center gap-1 overflow-visible rounded-xl p-0 align-middle transition-[transform,box-shadow,filter] duration-200 ease-out will-change-transform [backface-visibility:hidden] active:scale-[0.98] motion-reduce:transition-none sm:w-[78px]';
export const LOBBY_BOTTOM_NAV_SIDE_LABEL_CLASS =
  'relative z-[2] text-center text-[8px] font-semibold uppercase leading-none tracking-[0.12em] text-white/90';
export const LOBBY_BOTTOM_NAV_GLOSS_CLASS =
  'pointer-events-none absolute inset-x-0 top-0 z-[1] h-[34%] rounded-t-[inherit] bg-gradient-to-b from-white/[0.28] via-white/[0.08] to-transparent';
export const LOBBY_NOTIFICATION_BADGE_CLASS =
  'relative z-[9999] inline-flex h-[14px] min-w-[14px] items-center justify-center rounded-full bg-red-600 px-0.5 text-[8px] font-black leading-none text-white shadow ring-2 ring-[#0c1624]';
export const LOBBY_NOTIFICATION_BADGE_ABSOLUTE_CLASS =
  'absolute -right-0.5 -top-0.5 z-[9999] inline-flex h-[14px] min-w-[14px] items-center justify-center rounded-full bg-red-600 px-0.5 text-[8px] font-black leading-none text-white shadow ring-2 ring-[#0c1624]';
export const LOBBY_BOTTOM_NAV_FRIENDS_BADGE_CLASS = LOBBY_NOTIFICATION_BADGE_ABSOLUTE_CLASS;
/** Одна новая заявка — компактная красная точка (без цифры). */
export const LOBBY_BOTTOM_NAV_FRIENDS_DOT_WRAP_CLASS =
  'pointer-events-none absolute -right-0.5 top-0 z-[9999] flex h-4 w-4 items-center justify-center';
export const LOBBY_BOTTOM_NAV_FRIENDS_DOT_CLASS =
  'h-2.5 w-2.5 shrink-0 rounded-full bg-red-500 shadow-[0_0_0_2px_rgba(7,12,20,0.98)] ring-1 ring-red-400/55 motion-safe:animate-pulse';
/** Алиасы нейтрального имени (то же, что у «Друзья» в nav / тайлы наград). */
export const LOBBY_HUB_NOTIFICATION_DOT_WRAP_CLASS = LOBBY_BOTTOM_NAV_FRIENDS_DOT_WRAP_CLASS;
export const LOBBY_HUB_NOTIFICATION_DOT_CLASS = LOBBY_BOTTOM_NAV_FRIENDS_DOT_CLASS;
/** Точка в строке таба (inline, рядом с подписью). */
export const LOBBY_HUB_NOTIFICATION_INLINE_DOT_WRAP_CLASS =
  'relative z-[9999] ml-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center';

/**
 * Единый индикатор: 0 — ничего; 1 — пульсирующая красная точка; 2+ — pill с числом (max+).
 * @param {'absoluteCorner'|'inlineTab'} [layout] — absolute: угол кнопки/тайла; inlineTab: в табе рядом с текстом.
 */
export function LobbyHubNotificationIndicator({ count, layout = 'absoluteCorner', max = 9, className }) {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  if (n <= 0) return null;
  const cap = Math.max(1, Math.floor(Number(max) || 9));
  const pillText = n > cap ? `${cap}+` : String(n);

  if (n === 1) {
    const wrapClass =
      layout === 'inlineTab' ? LOBBY_HUB_NOTIFICATION_INLINE_DOT_WRAP_CLASS : LOBBY_HUB_NOTIFICATION_DOT_WRAP_CLASS;
    return (
      <span className={cn(wrapClass, className)} aria-hidden>
        <span className={LOBBY_HUB_NOTIFICATION_DOT_CLASS} />
      </span>
    );
  }

  if (layout === 'inlineTab') {
    return (
      <span className={cn(LOBBY_NOTIFICATION_BADGE_CLASS, 'ml-0.5', className)} aria-hidden>
        {pillText}
      </span>
    );
  }

  return (
    <span className={cn(LOBBY_NOTIFICATION_BADGE_ABSOLUTE_CLASS, className)} aria-hidden>
      {pillText}
    </span>
  );
}

export const LOBBY_BOTTOM_NAV_CENTER_WRAP_CLASS = 'relative z-10 flex justify-center self-center';
export const LOBBY_BOTTOM_NAV_CENTER_BUTTON_BASE_CLASS =
  'lobby-focus relative isolate m-0 flex h-[60px] w-[70px] shrink-0 flex-col items-center justify-center gap-0.5 overflow-hidden rounded-xl border p-0 align-middle text-white transition-[transform,box-shadow,filter] duration-200 ease-out will-change-transform [backface-visibility:hidden] active:scale-[0.98] motion-reduce:transition-none sm:w-[78px]';
export const LOBBY_BOTTOM_NAV_CENTER_GLOSS_CLASS =
  'pointer-events-none absolute inset-x-0 top-0 z-[1] h-[30%] rounded-t-[inherit] bg-gradient-to-b from-white/[0.35] via-white/[0.1] to-transparent';
export const LOBBY_BOTTOM_NAV_CENTER_CONTENT_CLASS = 'relative z-[2] flex flex-col items-center justify-center gap-0.5';
export const LOBBY_BOTTOM_NAV_CENTER_LABEL_CLASS =
  'max-w-[64px] text-center text-[6px] font-semibold uppercase leading-[1.02] tracking-[0.09em] text-white/95 sm:max-w-[70px] sm:text-[6.5px]';

/** Цветовые пресеты нижнего nav по хабу (Сека / Покер / Белот). */
export function lobbyBottomNavTheme(mode) {
  switch (normalizeLobbyHubMode(mode)) {
    case 'poker':
      return {
        shell: 'before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-emerald-300/30',
        left:
          'bg-gradient-to-b from-[#0a201a] via-[#08130f] to-[#03070a] shadow-[inset_0_1px_0_0_rgba(52,211,153,0.7),inset_1px_0_0_0_rgba(52,211,153,0.7),inset_0_-1px_0_0_rgba(0,0,0,0.55),inset_-1px_0_0_0_rgba(0,0,0,0.4),inset_0_0_0_-1px_rgba(0,0,0,0.35),inset_0_1px_0_0_rgba(255,255,255,0.12),0_0_20px_-4px_rgba(16,185,129,0.34)] hover:shadow-[inset_0_1px_0_0_rgba(110,231,183,0.9),inset_1px_0_0_0_rgba(110,231,183,0.9),inset_0_-1px_0_0_rgba(0,0,0,0.55),inset_-1px_0_0_0_rgba(0,0,0,0.4),inset_0_0_0_-1px_rgba(0,0,0,0.35),inset_0_1px_0_0_rgba(255,255,255,0.16),0_0_26px_-2px_rgba(16,185,129,0.44)]',
        leftIcon: 'text-emerald-200 drop-shadow-[0_0_8px_rgba(110,231,183,0.8)]',
        right:
          'bg-gradient-to-b from-[#102238] via-[#091220] to-[#04070d] shadow-[inset_0_1px_0_0_rgba(96,165,250,0.66),inset_-1px_0_0_0_rgba(96,165,250,0.66),inset_0_-1px_0_0_rgba(0,0,0,0.55),inset_1px_0_0_0_rgba(0,0,0,0.4),inset_0_0_0_-1px_rgba(0,0,0,0.35),inset_0_1px_0_0_rgba(255,255,255,0.1),0_0_20px_-4px_rgba(59,130,246,0.3)] hover:shadow-[inset_0_1px_0_0_rgba(147,197,253,0.82),inset_-1px_0_0_0_rgba(147,197,253,0.82),inset_0_-1px_0_0_rgba(0,0,0,0.55),inset_1px_0_0_0_rgba(0,0,0,0.4),inset_0_0_0_-1px_rgba(0,0,0,0.35),inset_0_1px_0_0_rgba(255,255,255,0.14),0_0_26px_-2px_rgba(59,130,246,0.42)]',
        rightIcon: 'text-sky-100 drop-shadow-[0_0_8px_rgba(147,197,253,0.75)]',
        centerFrame:
          'border-emerald-400/55 shadow-[0_0_0_1px_rgba(16,185,129,0.25),0_0_22px_rgba(16,185,129,0.46),0_0_42px_rgba(6,95,70,0.24),inset_0_1px_0_rgba(255,255,255,0.22),inset_0_-12px_24px_rgba(0,0,0,0.45)] hover:shadow-[0_0_0_1px_rgba(16,185,129,0.36),0_0_28px_rgba(16,185,129,0.56),0_0_48px_rgba(6,95,70,0.28),inset_0_1px_0_rgba(255,255,255,0.26),inset_0_-12px_24px_rgba(0,0,0,0.45)] hover:brightness-[1.03]',
        centerFill: 'bg-gradient-to-b from-[#34d399] via-[#059669] to-[#064e3b]',
        centerIcon: 'text-white drop-shadow-[0_0_12px_rgba(52,211,153,0.72)]',
      };
    case 'belot':
      return {
        shell: 'before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-fuchsia-300/30',
        left:
          'bg-gradient-to-b from-[#0d1829] via-[#080f18] to-[#03050a] shadow-[inset_0_1px_0_0_rgba(56,189,248,0.65),inset_1px_0_0_0_rgba(56,189,248,0.65),inset_0_-1px_0_0_rgba(0,0,0,0.55),inset_-1px_0_0_0_rgba(0,0,0,0.4),inset_0_0_0_-1px_rgba(0,0,0,0.35),inset_0_1px_0_0_rgba(255,255,255,0.12),0_0_20px_-4px_rgba(34,211,238,0.32)] hover:shadow-[inset_0_1px_0_0_rgba(103,232,249,0.85),inset_1px_0_0_0_rgba(103,232,249,0.85),inset_0_-1px_0_0_rgba(0,0,0,0.55),inset_-1px_0_0_0_rgba(0,0,0,0.4),inset_0_0_0_-1px_rgba(0,0,0,0.35),inset_0_1px_0_0_rgba(255,255,255,0.16),0_0_26px_-2px_rgba(34,211,238,0.42)]',
        leftIcon: 'text-amber-300 drop-shadow-[0_0_8px_rgba(252,211,77,0.85)]',
        right:
          'bg-gradient-to-b from-[#140f22] via-[#0a0712] to-[#03050a] shadow-[inset_0_1px_0_0_rgba(232,121,249,0.68),inset_-1px_0_0_0_rgba(232,121,249,0.68),inset_0_-1px_0_0_rgba(0,0,0,0.55),inset_1px_0_0_0_rgba(0,0,0,0.4),inset_0_0_0_-1px_rgba(0,0,0,0.35),inset_0_1px_0_0_rgba(255,255,255,0.1),0_0_20px_-4px_rgba(217,70,239,0.3)] hover:shadow-[inset_0_1px_0_0_rgba(245,208,254,0.82),inset_-1px_0_0_0_rgba(245,208,254,0.82),inset_0_-1px_0_0_rgba(0,0,0,0.55),inset_1px_0_0_0_rgba(0,0,0,0.4),inset_0_0_0_-1px_rgba(0,0,0,0.35),inset_0_1px_0_0_rgba(255,255,255,0.14),0_0_26px_-2px_rgba(217,70,239,0.42)]',
        rightIcon: 'text-fuchsia-100 drop-shadow-[0_0_8px_rgba(232,121,249,0.75)]',
        centerFrame:
          'border-fuchsia-400/55 shadow-[0_0_0_1px_rgba(244,114,182,0.22),0_0_22px_rgba(217,70,239,0.48),0_0_42px_rgba(124,58,237,0.18),inset_0_1px_0_rgba(255,255,255,0.22),inset_0_-12px_24px_rgba(0,0,0,0.45)] hover:shadow-[0_0_0_1px_rgba(244,114,182,0.35),0_0_28px_rgba(217,70,239,0.58),0_0_48px_rgba(124,58,237,0.22),inset_0_1px_0_rgba(255,255,255,0.26),inset_0_-12px_24px_rgba(0,0,0,0.45)] hover:brightness-[1.03]',
        centerFill: 'bg-gradient-to-b from-[#a78bfa] via-[#7c3aed] to-[#4c1d95]',
        centerIcon: 'text-white drop-shadow-[0_0_12px_rgba(192,132,252,0.75)]',
      };
    default:
      return {
        shell: 'before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-amber-300/35',
        left:
          'bg-gradient-to-b from-[#20180a] via-[#130f08] to-[#070503] shadow-[inset_0_1px_0_0_rgba(251,191,36,0.72),inset_1px_0_0_0_rgba(251,191,36,0.72),inset_0_-1px_0_0_rgba(0,0,0,0.55),inset_-1px_0_0_0_rgba(0,0,0,0.4),inset_0_0_0_-1px_rgba(0,0,0,0.35),inset_0_1px_0_0_rgba(255,255,255,0.12),0_0_20px_-4px_rgba(245,158,11,0.32)] hover:shadow-[inset_0_1px_0_0_rgba(252,211,77,0.9),inset_1px_0_0_0_rgba(252,211,77,0.9),inset_0_-1px_0_0_rgba(0,0,0,0.55),inset_-1px_0_0_0_rgba(0,0,0,0.4),inset_0_0_0_-1px_rgba(0,0,0,0.35),inset_0_1px_0_0_rgba(255,255,255,0.16),0_0_26px_-2px_rgba(245,158,11,0.42)]',
        leftIcon: 'text-amber-200 drop-shadow-[0_0_8px_rgba(252,211,77,0.85)]',
        right:
          'bg-gradient-to-b from-[#2a190d] via-[#160d08] to-[#090503] shadow-[inset_0_1px_0_0_rgba(251,146,60,0.7),inset_-1px_0_0_0_rgba(251,146,60,0.7),inset_0_-1px_0_0_rgba(0,0,0,0.55),inset_1px_0_0_0_rgba(0,0,0,0.4),inset_0_0_0_-1px_rgba(0,0,0,0.35),inset_0_1px_0_0_rgba(255,255,255,0.1),0_0_20px_-4px_rgba(249,115,22,0.3)] hover:shadow-[inset_0_1px_0_0_rgba(253,186,116,0.85),inset_-1px_0_0_0_rgba(253,186,116,0.85),inset_0_-1px_0_0_rgba(0,0,0,0.55),inset_1px_0_0_0_rgba(0,0,0,0.4),inset_0_0_0_-1px_rgba(0,0,0,0.35),inset_0_1px_0_0_rgba(255,255,255,0.14),0_0_26px_-2px_rgba(249,115,22,0.42)]',
        rightIcon: 'text-orange-100 drop-shadow-[0_0_8px_rgba(253,186,116,0.78)]',
        centerFrame:
          'border-amber-400/58 shadow-[0_0_0_1px_rgba(251,191,36,0.22),0_0_22px_rgba(245,158,11,0.48),0_0_42px_rgba(180,83,9,0.2),inset_0_1px_0_rgba(255,255,255,0.22),inset_0_-12px_24px_rgba(0,0,0,0.45)] hover:shadow-[0_0_0_1px_rgba(251,191,36,0.35),0_0_28px_rgba(245,158,11,0.58),0_0_48px_rgba(180,83,9,0.24),inset_0_1px_0_rgba(255,255,255,0.26),inset_0_-12px_24px_rgba(0,0,0,0.45)] hover:brightness-[1.03]',
        centerFill: 'bg-gradient-to-b from-[#f6c35a] via-[#d88a1f] to-[#8b5410]',
        centerIcon: 'text-white drop-shadow-[0_0_12px_rgba(251,191,36,0.72)]',
      };
  }
}

/** Унифицированный premium нижний nav для всех хабов. */
export function LobbyBottomNav({
  mode,
  onOpenGifts,
  onOpenFriends,
  onOpenCreateRoom,
  giftsNavBadgeCount = 0,
  friendsNavBadgeCount = 0,
  /** Макетные SVG без градиентных «плашек» (только главное лобби Секи). */
  visual = 'default',
}) {
  const navTheme = lobbyBottomNavTheme(mode);
  const isHomeArt = visual === 'homeArt';
  const friendsAriaLabel =
    friendsNavBadgeCount > 0
      ? `Моя братва, новых запросов закорефаниться: ${friendsNavBadgeCount}`
      : 'Моя братва';
  const giftsAriaLabel =
    giftsNavBadgeCount > 0
      ? `Подгоны от братвы, входящих подгонов: ${giftsNavBadgeCount}`
      : 'Подгоны от братвы';

  if (isHomeArt) {
    const artNavBtn =
      'lobby-focus relative m-0 flex w-full min-w-0 flex-col items-center justify-end border-0 bg-transparent p-0 shadow-none outline-none transition active:scale-[0.97]';
    const artNavImg =
      'pointer-events-none h-auto w-full max-h-[min(30vw,108px)] object-contain object-bottom select-none sm:max-h-[118px] lg:max-h-[124px]';
    const artNavGrid =
      'grid w-full min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_minmax(0,1fr)] gap-x-2 gap-y-0 sm:gap-x-3';
    const artNavFrameNarrow =
      'pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[70%] overflow-hidden rounded-[1.05rem] border-2 border-[#c9a227] bg-[#12100e] shadow-[inset_0_1px_0_rgba(255,255,255,0.14),inset_0_-1px_0_rgba(0,0,0,0.5)] sm:rounded-[1.15rem]';
    const ramkaTexUrl = LOBBY_HOME_ASSETS.decBottomNavRamka;
    return (
      <nav
        className={cn(
          LOBBY_BOTTOM_NAV_SHELL_CLASS,
          'border-t border-white/[0.08] bg-transparent shadow-none'
        )}
      >
        <div className="relative mx-auto w-full max-w-[500px] overflow-visible">
          <div className={artNavFrameNarrow} aria-hidden>
            <div className="absolute inset-0 bg-[#100d0b]" />
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${ramkaTexUrl})` }}
            />
          </div>
          <div className={cn(artNavGrid, 'relative z-[1] w-full items-end px-1 py-2 sm:px-2 sm:py-2.5')}>
            <div className="flex min-h-[min(22vw,5.75rem)] min-w-0 flex-col items-center justify-end sm:min-h-[6.25rem]">
              {typeof onOpenGifts === 'function' ? (
                <>
                  <img
                    src={LOBBY_HOME_ASSETS.decNavGift}
                    alt=""
                    aria-hidden
                    className="pointer-events-none relative z-[3] -translate-y-[7px] -mb-5 h-[52px] w-[50px] max-w-[46%] shrink-0 object-contain object-bottom select-none sm:-mb-[1.35rem] sm:h-[58px] sm:w-[54px]"
                    draggable={false}
                  />
                  <button type="button" onClick={() => onOpenGifts()} aria-label={giftsAriaLabel} className={cn(artNavBtn, 'relative z-[1]')}>
                    <LobbyHubNotificationIndicator count={giftsNavBadgeCount} layout="absoluteCorner" />
                    <img src={LOBBY_HOME_ASSETS.btnPodgonyBratva} alt="" className={artNavImg} draggable={false} />
                    <span className="sr-only">Подгоны от братвы</span>
                  </button>
                </>
              ) : (
                <div className="min-h-[min(22vw,5.75rem)] w-full" aria-hidden />
              )}
            </div>

            <div className="flex min-h-[min(24vw,6.25rem)] min-w-0 flex-col items-center justify-end sm:min-h-[6.75rem]">
              <img
                src={LOBBY_HOME_ASSETS.decNavDoor}
                alt=""
                aria-hidden
                className="pointer-events-none relative z-[3] -translate-y-[5px] -mb-[1.15rem] h-[48px] w-[46px] max-w-[40%] shrink-0 object-contain object-bottom select-none sm:-mb-5 sm:h-[54px] sm:w-[50px]"
                draggable={false}
              />
              <button
                type="button"
                onClick={() => onOpenCreateRoom?.()}
                aria-label="Создать комнату"
                className={cn(artNavBtn, 'relative z-[1] max-w-[min(100%,15.5rem)]')}
              >
                <img src={LOBBY_HOME_ASSETS.btnCreateRoomNav} alt="" className={artNavImg} draggable={false} />
                <span className="sr-only">Создать комнату</span>
              </button>
            </div>

            <div className="flex min-h-[min(22vw,5.75rem)] min-w-0 flex-col items-center justify-end sm:min-h-[6.25rem]">
              <button type="button" onClick={() => onOpenFriends?.()} aria-label={friendsAriaLabel} className={artNavBtn}>
                <LobbyHubNotificationIndicator count={friendsNavBadgeCount} layout="absoluteCorner" />
                <img src={LOBBY_HOME_ASSETS.btnBratvaNav} alt="" className={artNavImg} draggable={false} />
                <span className="sr-only">Братва</span>
              </button>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className={cn(LOBBY_BOTTOM_NAV_SHELL_CLASS, navTheme.shell)}>
      <div className={LOBBY_BOTTOM_NAV_DOCK_CLASS}>
        <div className={LOBBY_BOTTOM_NAV_GRID_CLASS}>
          <div className="flex min-w-0 justify-end">
            {typeof onOpenGifts === 'function' ? (
              <button
                type="button"
                onClick={() => onOpenGifts()}
                aria-label={giftsAriaLabel}
                className={cn(LOBBY_BOTTOM_NAV_SIDE_BUTTON_BASE_CLASS, navTheme.left)}
              >
                <LobbyHubNotificationIndicator count={giftsNavBadgeCount} layout="absoluteCorner" />
                <span className={LOBBY_BOTTOM_NAV_GLOSS_CLASS} aria-hidden />
                <Gift className={cn('relative z-[2] h-[22px] w-[22px]', navTheme.leftIcon)} strokeWidth={2} aria-hidden />
                <span className={LOBBY_BOTTOM_NAV_SIDE_LABEL_CLASS}>ПОДГОНЫ ОТ БРАТВЫ</span>
              </button>
            ) : (
              <div
                className="flex h-[60px] w-[70px] shrink-0 flex-col items-center justify-center sm:w-[78px]"
                aria-hidden
              />
            )}
          </div>

          <div className={LOBBY_BOTTOM_NAV_CENTER_WRAP_CLASS}>
            <button
              type="button"
              onClick={() => onOpenCreateRoom?.()}
              aria-label="Создать комнату"
              className={cn(LOBBY_BOTTOM_NAV_CENTER_BUTTON_BASE_CLASS, navTheme.centerFill, navTheme.centerFrame)}
            >
              <LobbyHubCreateRoomPourFill mode={mode} />
              <span className={LOBBY_BOTTOM_NAV_CENTER_GLOSS_CLASS} aria-hidden />
              <span className="lobby-create-room-anim" aria-hidden />
              <span className={LOBBY_BOTTOM_NAV_CENTER_CONTENT_CLASS}>
                <Plus className={cn('h-[18px] w-[18px] shrink-0 sm:h-5 sm:w-5', navTheme.centerIcon)} strokeWidth={2.35} aria-hidden />
                <span className={LOBBY_BOTTOM_NAV_CENTER_LABEL_CLASS}>СОЗДАТЬ КОМНАТУ</span>
              </span>
            </button>
          </div>

          <div className="flex min-w-0 justify-start">
            <button
              type="button"
              onClick={() => onOpenFriends?.()}
              aria-label={friendsAriaLabel}
              className={cn(LOBBY_BOTTOM_NAV_SIDE_BUTTON_BASE_CLASS, navTheme.right)}
            >
              <LobbyHubNotificationIndicator count={friendsNavBadgeCount} layout="absoluteCorner" />
              <span className={LOBBY_BOTTOM_NAV_GLOSS_CLASS} aria-hidden />
              <Users className={cn('relative z-[2] h-[22px] w-[22px]', navTheme.rightIcon)} strokeWidth={2} aria-hidden />
              <span className={LOBBY_BOTTOM_NAV_SIDE_LABEL_CLASS}>БРАТВА</span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}

/**
 * Два слоя заливки: при смене хаба (Сека↔Покер↔Белот) новый цвет «наливается» снизу вверх поверх предыдущего.
 * Предыдущий режим читается из `sessionStorage` при монтировании (компонент лобби при этом сменился).
 */
/** Разбивает `tile.accent` на классы рамки и градиента (для слоёв перелива). */
export function splitPlayTileAccent(accent) {
  if (!accent || typeof accent !== 'string') return { frame: '', fill: '' };
  const tokens = accent.trim().split(/\s+/).filter(Boolean);
  const frame = tokens.filter((t) => t.startsWith('border-')).join(' ');
  const fill = tokens.filter((t) => !t.startsWith('border-')).join(' ');
  return { frame, fill };
}

function playTileAccentStorageKey(tileIndex) {
  return `seka-lobby-play-tile-${tileIndex}-accent`;
}

function readPlayTileAccent(tileIndex) {
  try {
    return sessionStorage.getItem(playTileAccentStorageKey(tileIndex));
  } catch {
    return null;
  }
}

function writePlayTileAccent(tileIndex, accent) {
  try {
    sessionStorage.setItem(playTileAccentStorageKey(tileIndex), accent);
  } catch {
    /* ignore */
  }
}

/**
 * Перелив градиента плитки «Играть» при смене хаба (тот же приём, что у «Создать комнату»).
 * `tileIndex` — 0 первая плитка в ряду, 1 вторая (слот фиксирован между лобби).
 */
export function LobbyPlayModeTilePourFill({ tileIndex, accent }) {
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const prev = readPlayTileAccent(tileIndex);
    const prevOk = prev && typeof prev === 'string' && prev.includes('from-');
    if (prevOk && prev !== accent) {
      initialRef.current = { baseAccent: prev, overlayAccent: accent };
    } else {
      initialRef.current = { baseAccent: accent, overlayAccent: null };
    }
  }
  const ini = initialRef.current;

  const [baseAccent, setBaseAccent] = useState(ini.baseAccent);
  const [overlayAccent, setOverlayAccent] = useState(ini.overlayAccent);
  const [pourOpen, setPourOpen] = useState(false);
  const overlayAccentRef = useRef(overlayAccent);
  overlayAccentRef.current = overlayAccent;
  const pourTransitionHandledRef = useRef(false);

  useLayoutEffect(() => {
    if (!overlayAccent) return undefined;
    pourTransitionHandledRef.current = false;
    let reduce = false;
    try {
      reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      reduce = false;
    }
    if (reduce) {
      const om = overlayAccent;
      writePlayTileAccent(tileIndex, om);
      setBaseAccent(om);
      setOverlayAccent(null);
      setPourOpen(false);
      pourTransitionHandledRef.current = true;
      return undefined;
    }
    setPourOpen(false);
    let r2;
    const r1 = requestAnimationFrame(() => {
      r2 = requestAnimationFrame(() => setPourOpen(true));
    });
    return () => {
      cancelAnimationFrame(r1);
      if (r2) cancelAnimationFrame(r2);
    };
  }, [overlayAccent, tileIndex]);

  useEffect(() => {
    return () => {
      writePlayTileAccent(tileIndex, accent);
    };
  }, [tileIndex, accent]);

  const onPourTransitionEnd = (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.propertyName !== 'transform') return;
    if (pourTransitionHandledRef.current) return;
    const om = overlayAccentRef.current;
    if (!om) return;
    pourTransitionHandledRef.current = true;
    writePlayTileAccent(tileIndex, om);
    setBaseAccent(om);
    setOverlayAccent(null);
    setPourOpen(false);
  };

  const baseFill = splitPlayTileAccent(baseAccent).fill;

  return (
    <>
      <span
        className={cn(
          'pointer-events-none absolute inset-0 z-0 rounded-[inherit] bg-gradient-to-b',
          baseFill
        )}
        aria-hidden
      />
      {overlayAccent ? (
        <span
          aria-hidden
          onTransitionEnd={onPourTransitionEnd}
          className={cn(
            'lobby-create-room-pour-fill pointer-events-none absolute inset-0 z-0 rounded-[inherit] bg-gradient-to-b',
            splitPlayTileAccent(overlayAccent).fill,
            pourOpen && 'lobby-create-room-pour-fill--open'
          )}
        />
      ) : null}
    </>
  );
}

export function LobbyHubCreateRoomPourFill({ mode }) {
  const target = normalizeLobbyHubMode(mode);
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const prev = readLobbyHubLastMode();
    if (prev && prev !== target) {
      initialRef.current = { baseMode: prev, overlayMode: target };
    } else {
      initialRef.current = { baseMode: target, overlayMode: null };
    }
  }
  const ini = initialRef.current;

  const [baseMode, setBaseMode] = useState(ini.baseMode);
  const [overlayMode, setOverlayMode] = useState(ini.overlayMode);
  const [pourOpen, setPourOpen] = useState(false);
  const overlayRef = useRef(overlayMode);
  overlayRef.current = overlayMode;
  const pourTransitionHandledRef = useRef(false);

  useLayoutEffect(() => {
    if (!overlayMode) return undefined;
    pourTransitionHandledRef.current = false;
    let reduce = false;
    try {
      reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      reduce = false;
    }
    if (reduce) {
      writeLobbyHubLastMode(overlayMode);
      setBaseMode(overlayMode);
      setOverlayMode(null);
      setPourOpen(false);
      pourTransitionHandledRef.current = true;
      return undefined;
    }
    setPourOpen(false);
    let r2;
    const r1 = requestAnimationFrame(() => {
      r2 = requestAnimationFrame(() => setPourOpen(true));
    });
    return () => {
      cancelAnimationFrame(r1);
      if (r2) cancelAnimationFrame(r2);
    };
  }, [overlayMode]);

  useEffect(() => {
    return () => {
      writeLobbyHubLastMode(target);
    };
  }, [target]);

  const onPourTransitionEnd = (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.propertyName !== 'transform') return;
    if (pourTransitionHandledRef.current) return;
    const om = overlayRef.current;
    if (!om) return;
    pourTransitionHandledRef.current = true;
    writeLobbyHubLastMode(om);
    setBaseMode(om);
    setOverlayMode(null);
    setPourOpen(false);
  };

  return (
    <>
      <span
        className={cn(
          'pointer-events-none absolute inset-0 z-0 rounded-[inherit]',
          lobbyHubCreateRoomButtonFillClass(baseMode)
        )}
        aria-hidden
      />
      {overlayMode ? (
        <span
          aria-hidden
          onTransitionEnd={onPourTransitionEnd}
          className={cn(
            'lobby-create-room-pour-fill pointer-events-none absolute inset-0 z-0 rounded-[inherit]',
            lobbyHubCreateRoomButtonFillClass(overlayMode),
            pourOpen && 'lobby-create-room-pour-fill--open'
          )}
        />
      ) : null}
    </>
  );
}

export function LobbyProfileSection({ user, onLogout }) {
  return (
    <section className="mb-5 rounded-2xl border border-white/10 bg-gradient-to-br from-[#1a3358] via-[#132744] to-[#0d1a2e] p-4 shadow-lg">
      <div className="flex items-start gap-3">
        <div
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#2a4a7a] to-[#152238] text-xl font-bold text-white ring-2 ring-[#c9a227]/35"
          aria-hidden
        >
          {(user?.username || 'Г').charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-bold text-white">{user?.username || 'Гость'}</p>
          <p className="mt-0.5 text-xs text-[#8fa3bf]">Рейтинг и достижения — скоро</p>
          <div
            className="mt-3 h-2.5 overflow-hidden rounded-full bg-black/40 ring-1 ring-white/10"
            role="presentation"
            aria-hidden
          >
            <div className="h-full w-[58%] rounded-full bg-gradient-to-r from-[#c9a227] to-[#f5e6a8]" />
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-1.5">
          <button
            type="button"
            aria-label="Статистика (скоро)"
            title="Статистика"
            className="lobby-focus flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-black/20 text-[#b8c9e0] transition duration-150 hover:bg-white/10 hover:text-white"
            onClick={() => {}}
          >
            <BarChart3 className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            aria-label="Настройки (скоро)"
            title="Настройки"
            className="lobby-focus flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-black/20 text-[#b8c9e0] transition duration-150 hover:bg-white/10 hover:text-white"
            onClick={() => {}}
          >
            <Settings className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            aria-label="Выйти из аккаунта"
            title="Выйти"
            onClick={onLogout}
            className="lobby-focus flex h-9 w-9 items-center justify-center rounded-xl border border-red-500/30 bg-red-950/30 text-red-300 transition duration-150 hover:bg-red-900/40 hover:text-red-100"
          >
            <LogOut className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 border-t border-white/10 pt-3 text-center">
        <div>
          <p className="text-lg font-bold text-white">{user?.gamesPlayed ?? 0}</p>
          <p className="text-[10px] font-medium uppercase tracking-wider text-[#8fa3bf]">Игр</p>
        </div>
        <div>
          <p className="text-lg font-bold text-[#e8c547]">{user?.wins ?? 0}</p>
          <p className="text-[10px] font-medium uppercase tracking-wider text-[#8fa3bf]">Побед</p>
        </div>
        <div>
          <p className="text-lg font-bold text-white">{user?.winRate ?? 0}%</p>
          <p className="text-[10px] font-medium uppercase tracking-wider text-[#8fa3bf]">Винрейт</p>
        </div>
      </div>
    </section>
  );
}

/** `children` — например `LobbyGameSwitcher` между подписью «Играть» и плитками режимов. */
export function LobbyPlayModesRow({ onTileClick, socket, tiles, children }) {
  const list = tiles ?? MODE_TILES;
  const [tilePulseId, setTilePulseId] = useState(null);
  const [tilePulseTick, setTilePulseTick] = useState(0);

  useEffect(() => {
    if (!consumeLobbyModeSwitchPlayTilesOnce()) return;
    setTilePulseId('__all__');
    setTilePulseTick((v) => v + 1);
  }, []);

  return (
    <>
      {children ? (
        <div className="flex flex-col gap-3">
          <p className="px-1 text-xs font-semibold uppercase tracking-wider text-[#8fa3bf]">Играть</p>
          {children}
        </div>
      ) : (
        <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-[#8fa3bf]">Играть</p>
      )}
      <div className="lobby-play-modes-tiles lobby-mp-scroll mb-4 flex max-sm:snap-x max-sm:snap-mandatory gap-3 overflow-x-auto pb-1 sm:mb-6 sm:overflow-x-visible">
        {list.map((tile, tileIndex) => {
          const Icon = tile.icon;
          const { frame: tileFrame } = splitPlayTileAccent(tile.accent);
          const isOnlineTile = tile.id.includes('online');
          const isBotTile = tile.id === 'local' || tile.id.includes('bot');
          return (
            <button
              key={tile.id}
              type="button"
              aria-label={`${tile.label}: ${tile.sub}`}
              onClick={() => {
                setTilePulseId(tile.id);
                setTilePulseTick((v) => v + 1);
                onTileClick(tile);
              }}
              disabled={Boolean(tile.disabled) || (tile.id === 'bot' && !socket)}
              className={cn(
                'lobby-focus relative flex min-h-[118px] w-[min(42vw,150px)] shrink-0 snap-start flex-col items-center justify-center gap-2 overflow-hidden rounded-2xl border p-3 text-center shadow-lg transition duration-150 ease-out active:scale-[0.97] disabled:pointer-events-none disabled:opacity-40 sm:min-h-[128px] sm:w-[140px]',
                tileFrame
              )}
            >
              <LobbyPlayModeTilePourFill tileIndex={tileIndex} accent={tile.accent} />
              {tile.backgroundSrc ? (
                <span className="pointer-events-none absolute inset-0 z-[1] overflow-hidden" aria-hidden>
                  <img
                    src={tile.backgroundSrc}
                    alt=""
                    className={cn(
                      'h-full w-full object-cover object-center',
                      isBotTile
                        ? 'scale-[1.04] blur-[0.6px] brightness-[0.92] saturate-[0.88] contrast-[0.9]'
                        : 'scale-100'
                    )}
                    draggable={false}
                  />
                  <span className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(255,255,255,0.12),rgba(255,255,255,0.01)_32%,rgba(0,0,0,0.58)_74%)]" />
                  <span className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_22%,rgba(0,0,0,0.64)_72%)]" />
                  <span
                    className={cn(
                      'absolute inset-0 bg-gradient-to-b from-black/26 via-black/54 to-black/76',
                      isBotTile && 'from-black/34 via-black/58 to-black/78'
                    )}
                  />
                  <span className="absolute inset-0 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08),inset_34px_0_44px_rgba(0,0,0,0.62),inset_-34px_0_44px_rgba(0,0,0,0.62),inset_0_-26px_38px_rgba(0,0,0,0.6)]" />
                </span>
              ) : null}
              <span className="relative z-[2] flex flex-col items-center justify-center gap-2">
                {tile.animationSrc ? (
                  <span
                    className={cn(
                      'relative',
                      isBotTile
                          ? 'h-8 w-8 overflow-visible sm:h-9 sm:w-9'
                        : isOnlineTile
                          ? 'h-9 w-9 overflow-visible sm:h-10 sm:w-10'
                          : 'h-8 w-8 overflow-hidden sm:h-9 sm:w-9'
                    )}
                    aria-hidden
                  >
                    {isOnlineTile ? (
                      <span className="pointer-events-none absolute left-1/2 top-1/2 h-11 w-11 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/35 bg-white/10 shadow-[0_0_18px_rgba(255,255,255,0.28)]" />
                    ) : null}
                    <LobbyModeTileLottie
                      src={tile.animationSrc}
                      tileId={tile.id}
                      continuousLoop={isOnlineTile}
                      playSignal={tilePulseId === '__all__' || tilePulseId === tile.id ? tilePulseTick : 0}
                      style={
                        isOnlineTile
                          ? { filter: 'grayscale(1) brightness(3.1) contrast(1.28)' }
                          : undefined
                      }
                      className={cn(
                        'absolute left-1/2 top-1/2 h-full w-full -translate-x-1/2 -translate-y-1/2 drop-shadow-md',
                        isBotTile && 'scale-[1.72]',
                        isOnlineTile && 'scale-[3.15] opacity-100 drop-shadow-[0_0_18px_rgba(255,255,255,0.82)]'
                      )}
                    />
                  </span>
                ) : Icon ? (
                  <Icon className="h-8 w-8 text-white/95 drop-shadow-md sm:h-9 sm:w-9" strokeWidth={1.75} aria-hidden />
                ) : null}
                <span
                  className={cn(
                    'text-sm font-bold leading-tight text-white',
                    tile.id.includes('online') && 'tracking-[0.01em] drop-shadow-[0_1px_6px_rgba(0,0,0,0.72)]'
                  )}
                >
                  {tile.label}
                </span>
                <span
                  className={cn(
                    'text-[10px] font-medium text-white/70',
                    tile.id.includes('online') && 'drop-shadow-[0_1px_5px_rgba(0,0,0,0.65)]'
                  )}
                >
                  {tile.sub}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}

const FISKI_SIZES = {
  sm: { className: 'h-3.5 w-3.5', px: 14 },
  md: { className: 'h-4 w-4', px: 16 },
  /** Компактная фишка в шапке рядом с балансом (между md и lg). */
  bar: { className: 'h-7 w-7', px: 28 },
  lg: { className: 'h-8 w-8', px: 32 },
};

/** Общая зелёная «вкладка» + для плашек баланса в шапке лобби. */
function LobbyWalletGreenPlusTab() {
  return (
    <span
      className="flex w-[18px] shrink-0 items-center justify-center self-stretch border-l border-black/60 bg-gradient-to-b from-[#3ae49a] via-[#0bb87a] to-[#035c3f] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22)] sm:w-5"
      aria-hidden
    >
      <Plus className="h-3 w-3 shrink-0 stroke-[2.5]" strokeLinecap="round" />
    </span>
  );
}

const LOBBY_ASSET_BAR_SHELL =
  'lobby-focus flex h-[52px] shrink-0 items-stretch overflow-hidden rounded-md border border-[#070f18] shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition duration-150 ease-out hover:border-[#1a3550] active:scale-[0.98]';

/** Общая оболочка сегмента в одной полосе ресурсов (Miniclip): без скругления и внешней рамки у ячейки. */
const LOBBY_RAIL_SEGMENT_SHELL =
  'lobby-focus flex h-[52px] min-h-[52px] w-full min-w-0 shrink-0 items-stretch overflow-hidden rounded-none border-0 shadow-none transition hover:bg-white/[0.04] active:scale-[0.99]';

/** Иконка валюты SEKA в шапке лобби и в списке столов (`public/fiski.png`). */
export function LobbyFiskiIcon({ className, size = 'md' }) {
  const cfg = FISKI_SIZES[size] || FISKI_SIZES.md;
  return (
    <img
      src={`${process.env.PUBLIC_URL || ''}/fiski.png`}
      alt=""
      width={cfg.px}
      height={cfg.px}
      className={cn('shrink-0 object-contain', cfg.className, className)}
      draggable={false}
      aria-hidden
    />
  );
}

/**
 * Блок баланса SEKA в шапке: сверху `fiski.png`, снизу сумма, справа зелёная «+» (как `LobbyBucksBar`).
 * `rail` — встроить в общую полосу без отдельной рамки (как 8 Ball Pool).
 */
export function LobbyWalletBar({ balance, onOpenWallet, className, rail }) {
  const [imgMissing, setImgMissing] = useState(false);
  const formatted = Number(balance ?? 0).toLocaleString('ru-RU');
  const fiskiUrl = `${process.env.PUBLIC_URL || ''}/fiski.png`;

  return (
    <button
      type="button"
      onClick={onOpenWallet}
      aria-label={`Кошелёк, баланс ${formatted} SEKA`}
      className={cn(
        rail ? LOBBY_RAIL_SEGMENT_SHELL : LOBBY_ASSET_BAR_SHELL,
        'min-w-0 bg-[#0d2440]',
        !rail && 'hover:bg-[#102a48]',
        rail && 'hover:bg-[#102a48]',
        className
      )}
    >
      <span className="relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-2 py-0.5 pr-1 sm:min-w-[92px] sm:px-2.5">
        <span className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          <span className="absolute left-[10%] top-[10%] h-9 w-9 rounded-full bg-[#e8c547]/18 blur-2xl" />
          <span className="absolute right-[8%] top-[40%] h-5 w-5 rounded-full bg-amber-300/15 blur-lg" />
          <span className="absolute bottom-[6%] left-[38%] h-7 w-7 rounded-full bg-amber-500/12 blur-xl" />
        </span>
        <span className="relative z-[1] flex flex-col items-center gap-0.5">
          {!imgMissing ? (
            <img
              src={fiskiUrl}
              alt=""
              onError={() => setImgMissing(true)}
              className="h-[26px] w-auto max-w-[min(120px,100%)] object-contain object-center drop-shadow-[0_1px_4px_rgba(0,0,0,0.45)] sm:h-[28px]"
              draggable={false}
              aria-hidden
            />
          ) : (
            <LobbyFiskiIcon size="bar" className="drop-shadow-[0_1px_4px_rgba(0,0,0,0.45)]" />
          )}
          <span
            className={cn(
              'text-[12px] font-bold tabular-nums leading-none sm:text-[13px]',
              rail ? 'text-white' : 'text-[#f4e4a6]'
            )}
          >
            {formatted}
          </span>
        </span>
      </span>
      <LobbyWalletGreenPlusTab />
    </button>
  );
}

function formatUsdCompact(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: n % 1 !== 0 ? 2 : 0,
  }).format(n);
}

/**
 * Реальный денежный баланс (режим на деньги): стек купюр сверху, сумма ниже, справа «+», как в 8 Ball Pool.
 * `bucksBalance` — доллары с API (`user.bucksBalance` и т.п.). Картинка `public/bucks.png`.
 * `rail` — встроить в общую полосу ресурсов.
 */
export function LobbyBucksBar({ bucksBalance, onOpenWallet, className, rail }) {
  const [imgMissing, setImgMissing] = useState(false);
  const n = Number(bucksBalance ?? 0);
  const shown = rail
    ? (Number.isFinite(n) ? Math.round(n).toLocaleString('ru-RU') : '0')
    : formatUsdCompact(Number.isFinite(n) ? n : 0);
  const bucksUrl = `${process.env.PUBLIC_URL || ''}/bucks.png`;

  return (
    <button
      type="button"
      onClick={onOpenWallet}
      aria-label={`Реальный баланс ${shown}, пополнить`}
      className={cn(
        rail ? LOBBY_RAIL_SEGMENT_SHELL : LOBBY_ASSET_BAR_SHELL,
        'min-w-0 bg-[#0a1830]',
        !rail && 'hover:border-[#1e3d5c] hover:bg-[#0c1f3d]',
        rail && 'hover:bg-[#0c1f3d]',
        className
      )}
    >
      <span className="relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-2 py-0.5 pr-1 sm:min-w-[92px] sm:px-2.5">
        <span className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          <span className="absolute left-[8%] top-[12%] h-10 w-10 rounded-full bg-sky-400/25 blur-2xl" />
          <span className="absolute right-[6%] top-[38%] h-6 w-6 rounded-full bg-cyan-200/20 blur-lg" />
          <span className="absolute bottom-[8%] left-[35%] h-8 w-8 rounded-full bg-blue-400/15 blur-xl" />
        </span>
        <span className="relative z-[1] flex flex-col items-center gap-0.5">
          {!imgMissing ? (
            <img
              src={bucksUrl}
              alt=""
              onError={() => setImgMissing(true)}
              className="h-[26px] w-auto max-w-[min(120px,100%)] object-contain object-center sm:h-[28px]"
              draggable={false}
              aria-hidden
            />
          ) : (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-white/45">USD</span>
          )}
          <span className="text-[12px] font-bold tabular-nums leading-none text-white sm:text-[13px]">{shown}</span>
        </span>
      </span>
      <LobbyWalletGreenPlusTab />
    </button>
  );
}

/**
 * Магазин в шапке: сверху `store.png`, снизу «Магазин» белым (как на референсе 8 Ball Pool).
 * `rail` — сегмент в общей полосе без отдельной рамки.
 */
export function LobbyStoreHeaderButton({ onClick, className, rail }) {
  const [imgMissing, setImgMissing] = useState(false);
  const url = `${process.env.PUBLIC_URL || ''}/store.png`;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Магазин"
      className={cn(
        rail
          ? 'lobby-focus inline-flex h-full min-h-[52px] w-full min-w-0 flex-col items-center justify-center gap-0.5 rounded-none border-0 bg-[#0d1a2e] px-1.5 py-1 shadow-none transition hover:bg-[#122030] active:scale-[0.99]'
          : 'lobby-focus flex h-[52px] min-w-[52px] max-w-[64px] shrink-0 flex-col items-center justify-center gap-0.5 overflow-hidden rounded-md border border-[#070f18] bg-[#0d1a2e] px-1.5 py-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition duration-150 ease-out hover:border-[#1a3550] hover:bg-[#122030] active:scale-[0.98] sm:min-w-[56px]',
        className
      )}
    >
      <span className="flex min-h-0 flex-1 flex-col items-center justify-center gap-0.5">
        {!imgMissing ? (
          <img
            src={url}
            alt=""
            onError={() => setImgMissing(true)}
            className="pointer-events-none h-[26px] w-auto max-w-full shrink-0 object-contain object-center sm:h-[28px]"
            draggable={false}
            aria-hidden
          />
        ) : (
          <ShoppingBasket className="h-6 w-6 shrink-0 text-emerald-400 drop-shadow-sm" strokeWidth={2} aria-hidden />
        )}
        <span className="w-full truncate text-center text-[10px] font-bold leading-none text-white sm:text-[11px]">
          Магазин
        </span>
      </span>
    </button>
  );
}

/**
 * Сегмент «Рейтинги» в полосе ресурсов (иконка, таймер события, подпись). Таймер — заглушка до API.
 */
export function LobbyRatingsRailCell({ onClick = () => {}, className, countdownText = '6д 10ч 03м' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Рейтинги. ${countdownText}`}
      title="Скоро"
      className={cn(
        'lobby-focus flex h-[52px] min-h-[52px] w-full min-w-0 flex-col items-center justify-center gap-0.5 rounded-none border-0 bg-[#0d1a2e] px-1 py-0.5 shadow-none transition hover:bg-[#122030] active:scale-[0.99]',
        className
      )}
    >
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border-2 border-amber-500/85 bg-[#1a2d4a] shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]">
        <Star className="h-3.5 w-3.5 fill-rose-400 stroke-rose-200" strokeWidth={1.5} aria-hidden />
      </div>
      <span className="max-w-full truncate px-0.5 text-center text-[8px] font-bold leading-none text-[#f4e4a6] sm:text-[9px]">
        {countdownText}
      </span>
      <span className="text-[10px] font-bold leading-none text-white sm:text-[11px]">Рейтинги</span>
    </button>
  );
}

const strokeTextStyle = { WebkitTextStroke: '1.1px #000', paintOrder: 'stroke fill' };

/**
 * Ник и звезда уровня над полосой ресурсов в App (хабы Сека/Покер/Белот; звезда убрана из рейла).
 * @param {'online'|'reconnecting'|'offline'|undefined} connectionStatus — статус связи с лобби Colyseus.
 */
export function LobbyHubUsernameLine({ user, className, connectionStatus }) {
  const raw = (user?.username || '').trim();
  const level = Number(user?.level) || 1;
  const league = getPlayerLeague(level);
  const levelStr = String(level);
  const normalizedStatus =
    connectionStatus === 'reconnecting' || connectionStatus === 'offline' || connectionStatus === 'online'
      ? connectionStatus
      : null;
  const showSocketStatus = Boolean(normalizedStatus);
  const statusLabel =
    normalizedStatus === 'online'
      ? 'сервер онлайн'
      : normalizedStatus === 'reconnecting'
        ? 'переподключение'
        : 'сервер офлайн';
  if (!raw) return null;
  return (
    <div
      className={cn(
        'mb-2 flex w-full min-w-0 items-center justify-start gap-2 pl-1 pr-3 sm:gap-2.5 sm:pl-1.5',
        className
      )}
      title={
        showSocketStatus
          ? `${raw} · лига ${league.label} · ${statusLabel}`
          : `${raw} · лига ${league.label}`
      }
    >
      <div
        className="relative flex h-6 w-6 shrink-0 items-center justify-center sm:h-7 sm:w-7"
        aria-label={`Лига: ${league.label}`}
      >
        <Star
          className={cn('absolute inset-0 h-6 w-6 drop-shadow-md sm:h-7 sm:w-7', league.starClass)}
          strokeWidth={1.15}
          aria-hidden
        />
        <span
          className={cn(
            'relative z-[1] font-black tabular-nums leading-none text-white',
            levelStr.length >= 3 ? 'text-[8px] sm:text-[9px]' : 'text-[9px] sm:text-[10px]'
          )}
          style={strokeTextStyle}
        >
          {level}
        </span>
      </div>
      <span className="min-h-0 min-w-0 max-w-[min(100%,22rem)] flex-1 truncate text-left text-[12px] font-semibold tracking-tight text-white/90 sm:text-sm">
        {raw}
      </span>
      {showSocketStatus && (
        <div
          className={cn(
            'ml-auto inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold leading-none sm:gap-1.5 sm:px-2.5 sm:py-1 sm:text-[11px]',
            normalizedStatus === 'online'
              ? 'border-emerald-400/35 bg-emerald-950/30 text-emerald-300'
              : normalizedStatus === 'reconnecting'
                ? 'border-cyan-400/35 bg-cyan-950/30 text-cyan-200'
                : 'border-red-400/35 bg-red-950/30 text-red-300'
          )}
          role="status"
          aria-label={
            normalizedStatus === 'online'
              ? 'Онлайн: связь с сервером'
              : normalizedStatus === 'reconnecting'
                ? 'Переподключение к серверу'
                : 'Офлайн: нет связи с сервером'
          }
          title={
            normalizedStatus === 'online'
              ? 'Связь с сервером'
              : normalizedStatus === 'reconnecting'
                ? 'Идет переподключение'
                : 'Нет связи с сервером'
          }
        >
          {normalizedStatus === 'online' ? (
            <Wifi className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5" aria-hidden />
          ) : normalizedStatus === 'reconnecting' ? (
            <Loader2 className="h-3 w-3 shrink-0 animate-spin sm:h-3.5 sm:w-3.5" aria-hidden />
          ) : (
            <WifiOff className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5" aria-hidden />
          )}
          {normalizedStatus === 'online'
            ? 'Онлайн'
            : normalizedStatus === 'reconnecting'
              ? 'Переподключение'
              : 'Офлайн'}
        </div>
      )}
    </div>
  );
}

/**
 * Первый сегмент полосы лобби: аватар и XP (звезда уровня — в `LobbyHubUsernameLine`).
 */
export function LobbyProfileRailCell({ user, onClick = () => {}, className }) {
  const rawName = (user?.username || '').trim();
  const avatarUrl = String(user?.avatarUrl || '').trim() || GUEST_AVATAR_SRC;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Профиль: ${rawName || 'гость'}`}
      title={rawName ? `${rawName}` : 'Профиль'}
      className={cn(
        'lobby-focus relative flex h-full min-h-[60px] w-full min-w-0 items-center justify-center rounded-none border-0 bg-[#0d1a2e] px-1 py-1 shadow-none transition hover:bg-[#122030] active:scale-[0.99]',
        className
      )}
    >
      <div className="flex h-[52px] w-[52px] items-center justify-center overflow-hidden rounded-lg border border-amber-300/45 bg-gradient-to-br from-[#1f3b5d] to-[#10253f] shadow-[0_2px_8px_rgba(0,0,0,0.4)]">
        <img
          src={avatarUrl}
          alt=""
          className="h-full w-full scale-[1.4] object-cover"
          draggable={false}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = FALLBACK_GUEST_AVATAR_SRC;
          }}
        />
      </div>
    </button>
  );
}

/**
 * Полоса ресурсов главного лобби Секи (профиль | магазин | баксы | фишки) — для рендера в `App` над контентом.
 */
export function LobbySekaResourceRail({ user, balance, bucksBalance, onOpenWallet, onOpenShop, onProfileClick, className }) {
  const u = user || {};
  const bal = balance ?? u.balance ?? 0;
  const bucks = bucksBalance ?? u.bucksBalance ?? u.realBalance ?? u.cashBalance ?? 0;

  return (
    <header
      className={cn(
        'rounded-2xl border border-white/10 bg-gradient-to-b from-[#152238] to-[#0c1624] p-1 shadow-[0_8px_32px_rgba(0,0,0,0.45)] sm:p-1.5',
        className
      )}
    >
      <div className="flex h-[60px] w-full min-w-0 divide-x divide-black/55 overflow-hidden rounded-lg border border-black/50 bg-[#0a121f]/80">
        <div className="w-[70px] min-w-[70px] shrink-0">
          <LobbyProfileRailCell user={u} onClick={onProfileClick ?? (() => {})} />
        </div>
        <div className="w-[70px] min-w-[70px] shrink-0">
          <LobbyStoreHeaderButton rail onClick={onOpenShop} />
        </div>
        <div className="min-w-0 flex-1">
          <LobbyBucksBar rail bucksBalance={bucks} onOpenWallet={onOpenShop} />
        </div>
        <div className="min-w-0 flex-1">
          <LobbyWalletBar rail balance={bal} onOpenWallet={onOpenShop} />
        </div>
      </div>
    </header>
  );
}

/**
 * Ряд под рейлом: **ежедневные ништяки**, **почта**, опционально **кланы** (та же сетка/размеры, что у почты), затем слот `children` (напр. чат).
 */
const DAILY_REWARDS_IMG = `${process.env.PUBLIC_URL || ''}/daily-rewards.png`;
const PO4TA_IMG = `${process.env.PUBLIC_URL || ''}/po4ta.png`;
const CLANS_BUTTON_IMG = `${process.env.PUBLIC_URL || ''}/clans-button.png`;

/** Единый размер тайлов награды / почта / кланы (45×45 ≈ −25% от 60×60), картинка без cover. */
const LOBBY_HUB_TILE_PX = 45;
const LOBBY_HUB_TILE_FRAME =
  'lobby-focus relative m-0 inline-flex h-[46px] w-[52px] shrink-0 items-center justify-center overflow-visible rounded-lg p-0';
const LOBBY_HUB_TILE_IMG =
  'pointer-events-none block h-full w-full min-h-0 flex-1 object-contain object-center select-none';

export function LobbyHubRewardsMailRow({
  onDailyRewards,
  onMail,
  onClans,
  showDailyBadge = false,
  /** Число непрочитанных в игровой почте (0 — без точки; 2+ — цифра на бейдже). */
  mailUnreadCount = 0,
  className,
  children,
}) {
  const [dailyImgMissing, setDailyImgMissing] = useState(false);
  const [mailImgMissing, setMailImgMissing] = useState(false);
  const [clansImgMissing, setClansImgMissing] = useState(false);
  return (
    <div
      className={cn(
        'mt-1.5 flex min-w-0 items-center justify-start gap-2 overflow-x-hidden sm:mt-2 sm:gap-2',
        className
      )}
    >
      <button
        type="button"
        onClick={onDailyRewards ?? (() => {})}
        aria-label="Ежедневные ништяки"
        title="Ежедневные ништяки"
        className={cn(
          LOBBY_HUB_TILE_FRAME,
          'border-2 border-amber-500/75 bg-gradient-to-b from-[#1e3555] to-[#0c1624] shadow-[0_4px_16px_rgba(0,0,0,0.5)] transition hover:border-amber-400/90 hover:brightness-105 active:scale-[0.98]'
        )}
      >
        <LobbyHubNotificationIndicator count={showDailyBadge ? 1 : 0} layout="absoluteCorner" />
        {dailyImgMissing ? (
          <span className="flex min-h-0 flex-1 items-center justify-center">
            <Gift className="h-8 w-8 text-amber-200 drop-shadow" strokeWidth={1.65} aria-hidden />
          </span>
        ) : (
          <img
            src={DAILY_REWARDS_IMG}
            alt=""
            width={LOBBY_HUB_TILE_PX}
            height={LOBBY_HUB_TILE_PX}
            onError={() => setDailyImgMissing(true)}
            className={LOBBY_HUB_TILE_IMG}
            draggable={false}
            aria-hidden
          />
        )}
      </button>
      <button
        type="button"
        onClick={onMail ?? (() => {})}
        aria-label="Почта"
        title="Почта"
        className={cn(
          LOBBY_HUB_TILE_FRAME,
          'border border-white/15 bg-[#0a121f] text-white/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] transition hover:border-white/25 hover:bg-[#122030] active:scale-[0.98]'
        )}
      >
        <LobbyHubNotificationIndicator count={mailUnreadCount} layout="absoluteCorner" />
        {mailImgMissing ? (
          <span className="flex min-h-0 flex-1 items-center justify-center">
            <Mail className="h-8 w-8" strokeWidth={1.75} aria-hidden />
          </span>
        ) : (
          <img
            src={PO4TA_IMG}
            alt=""
            width={LOBBY_HUB_TILE_PX}
            height={LOBBY_HUB_TILE_PX}
            onError={() => setMailImgMissing(true)}
            className={LOBBY_HUB_TILE_IMG}
            draggable={false}
            aria-hidden
          />
        )}
      </button>
      {typeof onClans === 'function' ? (
        <button
          type="button"
          onClick={onClans}
          aria-label="Кланы"
          title="Кланы"
          className={cn(
            LOBBY_HUB_TILE_FRAME,
            'border border-white/15 bg-[#0a121f] text-white/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] transition hover:border-white/25 hover:bg-[#122030] active:scale-[0.98]'
          )}
        >
          {clansImgMissing ? (
            <span className="flex min-h-0 flex-1 items-center justify-center">
              <Users className="h-8 w-8" strokeWidth={2.15} aria-hidden />
            </span>
          ) : (
            <img
              src={CLANS_BUTTON_IMG}
              alt=""
              width={LOBBY_HUB_TILE_PX}
              height={LOBBY_HUB_TILE_PX}
              onError={() => setClansImgMissing(true)}
              className={LOBBY_HUB_TILE_IMG}
              draggable={false}
              aria-hidden
            />
          )}
        </button>
      ) : null}
      {children ? <div className="min-w-0 flex-1 overflow-hidden">{children}</div> : null}
    </div>
  );
}
